import { useState, useMemo, useEffect } from 'react';
import { useAdminStore } from '../store/adminStore';
import { Product } from '../../data/products';
import ImageUploader from '../components/ImageUploader';
import BarcodeGenerator from '../components/BarcodeGenerator';
import BarcodePrintTemplates from '../components/BarcodePrintTemplates';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Star,
  Tag,
  Package,
  Eye,
  EyeOff,
  Image,
  X,
  Save,
  AlertTriangle,
  QrCode,
  Barcode,
  Printer,
  CheckSquare,
} from 'lucide-react';

export default function AdminProducts() {
  const { products, categories, addProduct, updateProduct, deleteProduct, fetchProductsFromAPI } = useAdminStore();

  useEffect(() => {
    fetchProductsFromAPI();
  }, []);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [barcodeProduct, setBarcodeProduct] = useState<Product | null>(null);
  const [barcodeType, setBarcodeType] = useState<'qr' | 'barcode'>('qr');
  const [printTemplateProduct, setPrintTemplateProduct] = useState<Product | null>(null);
  const [showBulkPrint, setShowBulkPrint] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [isSelectMode, setIsSelectMode] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    salePrice: 0,
    regularPrice: 0,
    categories: [] as string[],
    images: [''],
    stock: 0,
    inStock: true,
    isCampaign: false,
    isNew: false,
    isFeatured: false,
    brand: 'T-RaX',
  });

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.brand.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || p.categories.includes(categoryFilter);
      return matchesSearch && matchesCategory;
    });
  }, [products, search, categoryFilter]);

  const openAddForm = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      salePrice: 0,
      regularPrice: 0,
      categories: [],
      images: [''],
      stock: 0,
      inStock: true,
      isCampaign: false,
      isNew: false,
      isFeatured: false,
      brand: 'T-RaX',
    });
    setShowForm(true);
  };

  const openEditForm = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      salePrice: product.salePrice,
      regularPrice: product.regularPrice,
      categories: [...product.categories],
      images: [...product.images],
      stock: product.stock,
      inStock: product.inStock,
      isCampaign: product.isCampaign,
      isNew: product.isNew,
      isFeatured: product.isFeatured,
      brand: product.brand,
    });
    setShowForm(true);
  };

  const handleSave = () => {
    if (editingProduct) {
      updateProduct(editingProduct.id, {
        ...formData,
        rating: editingProduct.rating,
        reviewCount: editingProduct.reviewCount,
      });
    } else {
      addProduct({
        ...formData,
        rating: 0,
        reviewCount: 0,
      });
    }
    setShowForm(false);
    setEditingProduct(null);
  };

  const handleDelete = (id: number) => {
    deleteProduct(id);
    setDeleteConfirm(null);
  };

  const toggleCategory = (catId: string) => {
    setFormData((prev) => ({
      ...prev,
      categories: prev.categories.includes(catId)
        ? prev.categories.filter((c) => c !== catId)
        : [...prev.categories, catId],
    }));
  };

  const toggleProductSelection = (id: number) => {
    setSelectedProducts((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map((p) => p.id));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ürün Yönetimi</h1>
          <p className="text-gray-500 mt-1">{products.length} ürün mevcut</p>
        </div>
        <div className="flex items-center gap-2">
          {isSelectMode && selectedProducts.length > 0 && (
            <button
              onClick={() => setShowBulkPrint(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl transition-colors"
            >
              <Printer className="w-5 h-5" />
              Toplu Yazdır ({selectedProducts.length})
            </button>
          )}
          <button
            onClick={() => {
              setIsSelectMode(!isSelectMode);
              setSelectedProducts([]);
            }}
            className={`flex items-center gap-2 px-4 py-2.5 font-semibold rounded-xl transition-colors ${
              isSelectMode
                ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <CheckSquare className="w-5 h-5" />
            {isSelectMode ? 'Seçimi Kaldır' : 'Toplu Seç'}
          </button>
          <button
            onClick={openAddForm}
            className="flex items-center gap-2 px-4 py-2.5 bg-brand-red hover:bg-red-700 text-white font-semibold rounded-xl transition-colors"
          >
            <Plus className="w-5 h-5" />
            Yeni Ürün
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Ürün ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red/30"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red/30"
        >
          <option value="all">Tüm Kategoriler</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredProducts.map((product) => (
          <div
            key={product.id}
            className={`bg-white rounded-2xl border overflow-hidden hover:shadow-lg transition-shadow group ${
              selectedProducts.includes(product.id) ? 'border-purple-400 ring-2 ring-purple-100' : 'border-gray-200'
            }`}
          >
            {/* Image */}
            <div className="relative h-48 bg-gray-100">
              {/* Selection Checkbox */}
              {isSelectMode && (
                <button
                  onClick={(e) => { e.stopPropagation(); toggleProductSelection(product.id); }}
                  className={`absolute top-2 left-2 z-10 w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-colors ${
                    selectedProducts.includes(product.id)
                      ? 'bg-purple-600 border-purple-600 text-white'
                      : 'bg-white/90 border-gray-300 hover:border-purple-400'
                  }`}
                >
                  {selectedProducts.includes(product.id) && (
                    <CheckSquare className="w-4 h-4" />
                  )}
                </button>
              )}
              {product.images[0] ? (
                <img
                  src={product.images[0]}
                  alt={product.name}
                  className="w-full h-full object-contain p-4"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Image className="w-12 h-12 text-gray-300" />
                </div>
              )}
              {/* Badges */}
              <div className="absolute top-2 left-2 flex flex-wrap gap-1">
                {product.isCampaign && (
                  <span className="px-2 py-0.5 bg-brand-red text-white text-xs font-bold rounded-full">
                    Kampanya
                  </span>
                )}
                {product.isNew && (
                  <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded-full">
                    Yeni
                  </span>
                )}
                {product.isFeatured && (
                  <span className="px-2 py-0.5 bg-yellow-500 text-white text-xs font-bold rounded-full">
                    Öne Çıkan
                  </span>
                )}
              </div>
              {/* Quick Actions */}
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                <button
                  onClick={() => setBarcodeProduct(product)}
                  className="p-2 bg-white/90 hover:bg-white rounded-lg shadow-sm text-gray-600 hover:text-purple-600 transition-colors"
                  title="Barkod/QR"
                >
                  <QrCode className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPrintTemplateProduct(product)}
                  className="p-2 bg-white/90 hover:bg-white rounded-lg shadow-sm text-gray-600 hover:text-brand-red transition-colors"
                  title="Etiket Yazdır"
                >
                  <Printer className="w-4 h-4" />
                </button>
                <button
                  onClick={() => openEditForm(product)}
                  className="p-2 bg-white/90 hover:bg-white rounded-lg shadow-sm text-gray-600 hover:text-blue-600 transition-colors"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setDeleteConfirm(product.id)}
                  className="p-2 bg-white/90 hover:bg-white rounded-lg shadow-sm text-gray-600 hover:text-red-600 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Info */}
            <div className="p-4">
              <p className="text-xs text-gray-500 mb-1">{product.brand}</p>
              <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-2">
                {product.name}
              </h3>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg font-bold text-brand-red">
                  ₺{product.salePrice.toLocaleString('tr-TR')}
                </span>
                {product.salePrice < product.regularPrice && (
                  <span className="text-sm text-gray-400 line-through">
                    ₺{product.regularPrice.toLocaleString('tr-TR')}
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <Package className="w-3 h-3" />
                  Stok: {product.stock}
                </span>
                <span className="flex items-center gap-1">
                  <Star className="w-3 h-3 text-yellow-500" />
                  {product.rating}
                </span>
              </div>
            </div>

            {/* Delete Confirmation */}
            {deleteConfirm === product.id && (
              <div className="p-4 bg-red-50 border-t border-red-100">
                <p className="text-sm text-red-700 mb-2">Bu ürünü silmek istediğinize emin misiniz?</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleDelete(product.id)}
                    className="flex-1 px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700"
                  >
                    Sil
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    className="flex-1 px-3 py-1.5 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300"
                  >
                    İptal
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Product Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingProduct ? 'Ürünü Düzenle' : 'Yeni Ürün Ekle'}
              </h2>
              <button
                onClick={() => { setShowForm(false); setEditingProduct(null); }}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Temel Bilgiler</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Ürün Adı *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                      placeholder="Ürün adını girin"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Açıklama</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20 resize-none"
                      placeholder="Ürün açıklaması"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Marka</label>
                      <input
                        type="text"
                        value={formData.brand}
                        onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Stok Miktarı</label>
                      <input
                        type="number"
                        value={formData.stock}
                        onChange={(e) => setFormData({ ...formData, stock: parseInt(e.target.value) || 0 })}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Fiyatlandırma</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Satış Fiyatı (₺) *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.salePrice}
                      onChange={(e) => setFormData({ ...formData, salePrice: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Normal Fiyat (₺)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.regularPrice}
                      onChange={(e) => setFormData({ ...formData, regularPrice: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                    />
                  </div>
                </div>
              </div>

              {/* Categories */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Kategoriler</h3>
                <div className="flex flex-wrap gap-2">
                  {categories.filter(c => c.id !== 'kendi-setini-olustur').map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => toggleCategory(cat.id)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        formData.categories.includes(cat.id)
                          ? 'bg-brand-red text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Image Upload */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Görseller</h3>
                <ImageUploader
                  images={formData.images}
                  onChange={(images) => setFormData({ ...formData, images })}
                  maxImages={5}
                />
              </div>

              {/* Toggles */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Durum</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'inStock', label: 'Stokta', icon: Package },
                    { key: 'isCampaign', label: 'Kampanyalı', icon: Tag },
                    { key: 'isNew', label: 'Yeni Ürün', icon: Star },
                    { key: 'isFeatured', label: 'Öne Çıkan', icon: Eye },
                  ].map((toggle) => (
                    <label
                      key={toggle.key}
                      className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={(formData as any)[toggle.key]}
                        onChange={(e) => setFormData({ ...formData, [toggle.key]: e.target.checked })}
                        className="w-4 h-4 text-brand-red rounded focus:ring-brand-red/20"
                      />
                      <toggle.icon className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">{toggle.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => { setShowForm(false); setEditingProduct(null); }}
                className="px-4 py-2.5 text-gray-700 hover:bg-gray-100 rounded-xl font-medium transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleSave}
                disabled={!formData.name || formData.salePrice <= 0}
                className="flex items-center gap-2 px-6 py-2.5 bg-brand-red hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors"
              >
                <Save className="w-4 h-4" />
                {editingProduct ? 'Güncelle' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Print Template Modal */}
      {printTemplateProduct && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Etiket Yazdır</h2>
                <p className="text-sm text-gray-500 truncate max-w-[300px]">{printTemplateProduct.name}</p>
              </div>
              <button
                onClick={() => setPrintTemplateProduct(null)}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <BarcodePrintTemplates
                product={printTemplateProduct}
                mode="single"
                onClose={() => setPrintTemplateProduct(null)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Bulk Print Modal */}
      {showBulkPrint && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Toplu Etiket Yazdır</h2>
                <p className="text-sm text-gray-500">{selectedProducts.length} ürün seçildi</p>
              </div>
              <button
                onClick={() => setShowBulkPrint(false)}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <BarcodePrintTemplates
                products={filteredProducts.filter((p) => selectedProducts.includes(p.id))}
                mode="bulk"
                onClose={() => setShowBulkPrint(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Barcode/QR Modal */}
      {barcodeProduct && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Barkod / QR Kod</h2>
                <p className="text-sm text-gray-500 truncate max-w-[300px]">{barcodeProduct.name}</p>
              </div>
              <button
                onClick={() => setBarcodeProduct(null)}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Type Toggle */}
            <div className="flex gap-2 px-6 pt-4">
              <button
                onClick={() => setBarcodeType('qr')}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  barcodeType === 'qr'
                    ? 'bg-brand-red text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <QrCode className="w-4 h-4" />
                QR Kod
              </button>
              <button
                onClick={() => setBarcodeType('barcode')}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  barcodeType === 'barcode'
                    ? 'bg-brand-red text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Barcode className="w-4 h-4" />
                Barkod
              </button>
            </div>

            <div className="p-6">
              <BarcodeGenerator
                data={`TRX-${barcodeProduct.id}-${barcodeProduct.name.substring(0, 20)}`}
                label={barcodeProduct.name}
                type={barcodeType}
                onClose={() => setBarcodeProduct(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
